import { useChatStore } from '@widgets/messenger/stores/useChatStore'
import messengerApiService from '@widgets/messenger/services/messengerApiService'
import messengerMessageListItemCastService from '@widgets/messenger/services/messengerMessageListItemCastService'
import type { MessengerAutoReplyMessageListItem, MessengerMessageListItem } from '@widgets/messenger/types/chat/message'
import { useWidgetStore } from '@widgets/messenger/stores/useWidgetStore'
import { reverse } from 'lodash-es'
import { WebWidgetChannelType } from '@/data/models/domain/messages/settings/webWidget/types'

export const useWidgetChatPages = () => {
  const chatStore = useChatStore()
  const widgetStore = useWidgetStore()

  const fetchNextPage = async () => {
    const lastId = chatStore.messageListItems.value[0]?.id
    if (!lastId || typeof lastId !== 'number') {
      return
    }
    await fetchPage(lastId)
  }

  const fetchPage = async (lastId?: number, applyMethod: 'unshift' | 'merge' = 'unshift') => {
    chatStore.patchMessageListState({
      isNextPageLoading: true,
    })
    try {
      const { data } = await messengerApiService.getMessages(chatStore.chatToken.value, lastId)
      const items = messengerMessageListItemCastService.castRestApiMessages(data.items)
      if (applyMethod === 'merge') {
        chatStore.mergeMessageListItems(postProcessItems(items))
      } else {
        chatStore.unshiftMessageListItems(postProcessItems(items))
      }
      chatStore.patchMessageListState({
        isAllMessagesLoaded: data.pagination.pageCount === data.pagination.page,
      })
    } finally {
      chatStore.patchMessageListState({
        isNextPageLoading: false,
      })
    }
  }

  const postProcessItems = (items: MessengerMessageListItem[]) => {
    const result = reverse(items)
    return insertAutoReplyMessage(result)
  }

  const insertAutoReplyMessage = (items: MessengerMessageListItem[]) => {
    const initialMessageIndex = items.findIndex((item) => item.type === 'initial')
    if (initialMessageIndex === -1) {
      return items
    }

    const autoReplyMessageListItem: MessengerAutoReplyMessageListItem = {
      type: 'autoReply',
      id: 'autoReply',
      messageTime: new Date().toISOString(),
      text: widgetStore.widgetSettings.value.channels[WebWidgetChannelType.CHAT].autoReplyMessage,
    }

    const result = [...items]
    result.splice(initialMessageIndex + 1, 0, autoReplyMessageListItem)
    return result
  }

  return {
    fetchNextPage,
    fetchPage,
  }
}
